import type { WysiwygEditorOption } from './types';

interface WysiwygDestinationRecord {
  id: string;
  hotelId: string;
  title: string;
}

export type WysiwygDestinationFolderRecord = WysiwygDestinationRecord;

export interface WysiwygDestinationPageRecord extends WysiwygDestinationRecord {
  categoryId: string;
  active?: boolean;
  originalPageId?: string;
}

export interface WysiwygDestinationItemRecord extends WysiwygDestinationRecord {
  categoryId: string;
  pageIds: string[];
  active: boolean;
}

export interface WysiwygDestinationFallbackLabels {
  folder: string;
  page: string;
  item: string;
}

interface BuildWysiwygLinkDestinationsOptions {
  hotelId: string;
  folders: WysiwygDestinationFolderRecord[];
  pages: WysiwygDestinationPageRecord[];
  items: WysiwygDestinationItemRecord[];
  fallbackLabels: WysiwygDestinationFallbackLabels;
}

interface PagePlacement {
  sourcePageId: string;
  option: WysiwygEditorOption;
}

const collators = new Map<string, Intl.Collator>();

export function buildWysiwygLinkDestinations({
  hotelId,
  folders,
  pages,
  items,
  fallbackLabels,
}: BuildWysiwygLinkDestinationsOptions) {
  const folderById = new Map(folders.map((folder) => [folder.id, folder]));
  const pageById = new Map(pages.map((page) => [page.id, page]));

  const pagePlacements = buildPagePlacements({
    hotelId,
    pages,
    pageById,
    folderById,
    fallbackLabels,
  });

  return {
    folders: buildFolderOptions(hotelId, folders, fallbackLabels),
    pages: pagePlacements.map((placement) => placement.option),
    items: buildItemOptions({
      hotelId,
      items,
      pagePlacements,
      folderById,
      fallbackLabels,
    }),
  };
}

function buildFolderOptions(
  hotelId: string,
  folders: WysiwygDestinationFolderRecord[],
  fallbackLabels: WysiwygDestinationFallbackLabels
) {
  return folders
    .filter((folder) => folder.hotelId === hotelId)
    .map((folder) => ({
      value: folder.id,
      label: titleOrFallback(folder.title, fallbackLabels.folder, folder.id),
    }));
}

function buildPagePlacements({
  hotelId,
  pages,
  pageById,
  folderById,
  fallbackLabels,
}: Pick<
  BuildWysiwygLinkDestinationsOptions,
  'hotelId' | 'pages' | 'fallbackLabels'
> & {
  pageById: Map<string, WysiwygDestinationPageRecord>;
  folderById: Map<string, WysiwygDestinationFolderRecord>;
}): PagePlacement[] {
  return pages
    .filter((page) => page.hotelId === hotelId)
    .map((page) => {
      const originalPage = page.originalPageId
        ? pageById.get(page.originalPageId)
        : undefined;
      const sourcePage = originalPage ?? page;

      return {
        sourcePageId: page.originalPageId ?? page.id,
        option: {
          value: page.id,
          label: titleOrFallback(
            sourcePage.title,
            fallbackLabels.page,
            page.id
          ),
          active: page.active !== false,
          contextLabel: folderTitle(
            folderById.get(page.categoryId),
            fallbackLabels.folder,
            page.categoryId
          ),
          folderIds: [page.categoryId],
        },
      };
    });
}

function buildItemOptions({
  hotelId,
  items,
  pagePlacements,
  folderById,
  fallbackLabels,
}: Pick<
  BuildWysiwygLinkDestinationsOptions,
  'hotelId' | 'items' | 'fallbackLabels'
> & {
  pagePlacements: PagePlacement[];
  folderById: Map<string, WysiwygDestinationFolderRecord>;
}) {
  const shadowSourcePageIds = new Set(
    pagePlacements
      .filter(({ option, sourcePageId }) => option.value !== sourcePageId)
      .map(({ sourcePageId }) => sourcePageId)
  );
  const placementsBySourcePageId = new Map<string, PagePlacement[]>();

  for (const placement of pagePlacements) {
    for (const pageId of new Set([
      placement.option.value,
      placement.sourcePageId,
    ])) {
      const placements = placementsBySourcePageId.get(pageId) ?? [];
      placements.push(placement);
      placementsBySourcePageId.set(pageId, placements);
    }
  }
  return items.flatMap((item): WysiwygEditorOption[] => {
    const isLocalItem = item.hotelId === hotelId;
    const isReachableSharedItem = item.pageIds.some((pageId) =>
      shadowSourcePageIds.has(pageId)
    );

    if (!isLocalItem && !isReachableSharedItem) return [];

    const placements = item.pageIds.flatMap(
      (pageId) => placementsBySourcePageId.get(pageId) ?? []
    );
    const pageIds = [
      ...new Set(placements.map((placement) => placement.option.value)),
    ];
    const placementFolderIds = placements.flatMap(
      (placement) => placement.option.folderIds ?? []
    );
    const folderIds = [
      ...new Set([
        ...(isLocalItem ? [item.categoryId] : []),
        ...placementFolderIds,
      ]),
    ];
    const contextLabel = folderIds
      .map((folderId) =>
        folderTitle(folderById.get(folderId), fallbackLabels.folder, folderId)
      )
      .join(', ');

    return [
      {
        value: item.id,
        label: titleOrFallback(item.title, fallbackLabels.item, item.id),
        active: item.active !== false,
        contextLabel,
        folderIds,
        pageIds,
      },
    ];
  });
}

export function sortAndDisambiguateOptions(
  options: WysiwygEditorOption[],
  localeKey: string
) {
  const collator = collatorFor(localeKey);
  const baseLabelCounts = labelCounts(options);
  const withContext = options.map((option) => ({
    ...option,
    // These separators intentionally stay locale-neutral; only the labels are localized.
    label:
      (baseLabelCounts.get(labelKey(option.label)) ?? 0) > 1 &&
      option.contextLabel
        ? `${option.label} — ${option.contextLabel}`
        : option.label,
  }));
  const contextualLabelCounts = labelCounts(withContext);

  return withContext
    .map((option) => ({
      ...option,
      label:
        (contextualLabelCounts.get(labelKey(option.label)) ?? 0) > 1
          ? `${option.label} (#${option.value})`
          : option.label,
    }))
    .sort(
      (left, right) =>
        collator.compare(left.label, right.label) ||
        collator.compare(left.value, right.value)
    );
}

function collatorFor(localeKey: string) {
  let collator = collators.get(localeKey);

  if (!collator) {
    collator = new Intl.Collator(localeKey, {
      numeric: true,
      sensitivity: 'base',
    });
    collators.set(localeKey, collator);
  }

  return collator;
}

function titleOrFallback(title: string, typeLabel: string, id: string) {
  return title.trim() || `${typeLabel} ${id}`;
}

function folderTitle(
  folder: WysiwygDestinationFolderRecord | undefined,
  fallbackLabel: string,
  folderId: string
) {
  return titleOrFallback(folder?.title ?? '', fallbackLabel, folderId);
}

function labelCounts(options: WysiwygEditorOption[]) {
  const counts = new Map<string, number>();

  options.forEach((option) => {
    const key = labelKey(option.label);

    counts.set(key, (counts.get(key) ?? 0) + 1);
  });

  return counts;
}

function labelKey(label: string) {
  return label.trim().toLowerCase();
}
